import type { ComponentProps } from 'react';
import type { DayButton } from 'react-day-picker';

import { cn } from '@repo/lib/cn';
import { format } from 'date-fns';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { DayPicker, getDefaultClassNames } from 'react-day-picker';
import { pl } from 'react-day-picker/locale';

import { Button, buttonVariants } from '@/components/ui/button';

export function Calendar(calendarProps: ComponentProps<typeof DayPicker>) {
  const { className, classNames, showOutsideDays = true, fixedWeeks = true, captionLayout = 'label', formatters, components, locale = pl, ...props } = calendarProps;

  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      fixedWeeks={fixedWeeks}
      locale={locale}
      showOutsideDays={showOutsideDays}
      className={cn(
        'bg-transparent group/calendar [--cell-size:--spacing(8)]',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatCaption: (date) => format(date, 'MMM yyyy'),
        formatMonthDropdown: (date) => format(date, 'MMMM'),
        ...formatters,
      }}
      classNames={{
        root: cn('w-fit', defaultClassNames.root),
        months: cn('relative flex flex-col gap-3 md:flex-row', defaultClassNames.months),
        month: cn('flex w-full flex-col gap-5', defaultClassNames.month),
        nav: cn('absolute inset-x-0 top-0 flex w-full items-center justify-between gap-1', defaultClassNames.nav),
        button_previous: cn(buttonVariants({ variant: 'ghost' }), 'h-9 min-h-9 w-9 min-w-9 p-0 select-none aria-disabled:opacity-60', defaultClassNames.button_previous),
        button_next: cn(buttonVariants({ variant: 'ghost' }), 'h-9 min-h-9 w-9 min-w-9 p-0 select-none aria-disabled:opacity-60', defaultClassNames.button_next),
        month_caption: cn('flex h-(--cell-size) w-full items-center justify-center px-(--cell-size)', defaultClassNames.month_caption),
        dropdowns: cn('flex h-(--cell-size) w-full items-center justify-center gap-1.5 text-sm font-medium', defaultClassNames.dropdowns),
        dropdown_root: cn('has-focus:border-focus-ring border-input has-focus:focus-ring/50 relative rounded-md border shadow-xs has-focus:ring-2', defaultClassNames.dropdown_root),
        dropdown: cn('absolute inset-0 opacity-0', defaultClassNames.dropdown),
        caption_label: cn(
          'font-medium select-none',
          captionLayout === 'label' ? 'text-sm' : '[&>svg]:text-muted-foreground flex h-8 items-center gap-1 rounded-md pr-1 pl-2 text-sm [&>svg]:size-3.5',
          defaultClassNames.caption_label
        ),
        table: 'w-full border-collapse',
        weekdays: cn('flex', defaultClassNames.weekdays),
        weekday: cn('text-muted-foreground flex-1 rounded-md text-xs font-normal select-none', defaultClassNames.weekday),
        week: cn('mt-2 flex w-full', defaultClassNames.week),
        week_number_header: cn('w-(--cell-size) select-none', defaultClassNames.week_number_header),
        week_number: cn('text-muted-foreground text-xs select-none', defaultClassNames.week_number),
        day: cn(
          'group/day relative aspect-square h-full w-full p-0 text-center select-none [&:first-child[data-selected=true]_button]:rounded-l-md [&:last-child[data-selected=true]_button]:rounded-r-md',
          defaultClassNames.day
        ),
        range_start: cn('bg-gray text-gray-foreground rounded-l-md', defaultClassNames.range_start),
        range_middle: cn('rounded-none', defaultClassNames.range_middle),
        range_end: cn('bg-gray text-gray-foreground rounded-r-md', defaultClassNames.range_end),
        today: cn('bg-accent text-accent-foreground rounded-md data-[selected=true]:rounded-none', defaultClassNames.today),
        outside: cn('text-muted-foreground aria-selected:text-muted-foreground', defaultClassNames.outside),
        disabled: cn('text-muted-foreground opacity-50', defaultClassNames.disabled),
        hidden: cn('invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: (rootProps) => {
          const { rootRef, ...props } = rootProps;

          return <div data-slot="calendar" ref={rootRef} {...props} />;
        },
        Chevron: (chevronProps) => {
          const { className, orientation, ...props } = chevronProps;
          if (orientation === 'left') {
            return <ChevronLeftIcon className={cn('size-3.5', className)} {...props} />;
          }

          if (orientation === 'right') {
            return <ChevronRightIcon className={cn('size-3.5', className)} {...props} />;
          }

          return <ChevronDownIcon className={cn('size-4', className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        WeekNumber: (weekNumberProps) => {
          const { children, week: _week, ...props } = weekNumberProps;
          return (
            <td {...props}>
              <div className="flex size-(--cell-size) items-center justify-center text-center">{children}</div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

export function CalendarDayButton(calendarDayButtonProps: ComponentProps<typeof DayButton>) {
  const { day, modifiers, className, ...props } = calendarDayButtonProps;
  const defaultClassNames = getDefaultClassNames();

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      type={'button'}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={modifiers.selected && !modifiers.range_start && !modifiers.range_end && !modifiers.range_middle}
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'data-[selected-single=true]:bg-gray data-[selected-single=true]:text-gray-foreground data-[range-middle=true]:bg-accent/50 data-[range-middle=true]:text-accent-foreground/75 data-[range-start=true]:bg-gray data-[range-start=true]:text-gray-foreground data-[range-end=true]:bg-gray data-[range-end=true]:text-gray-foreground group-data-[focused=true]/day:border-focus-ring group-data-[focused=true]/day:focus-ring-ring/50 flex aspect-square size-auto w-full min-w-(--cell-size) flex-col gap-1 leading-none font-normal group-data-[focused=true]/day:relative group-data-[focused=true]/day:z-10 group-data-[focused=true]/day:ring-2 data-[range-end=true]:rounded-md data-[range-end=true]:rounded-r-md data-[range-middle=true]:rounded-none data-[range-start=true]:rounded-md data-[range-start=true]:rounded-l-md [&>span]:text-xs [&>span]:opacity-75',
        modifiers.outside && 'text-muted-foreground',
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}
